import {
  StateNodeConfig,
  StateSchema,
  ConditionPredicate,
  MachineOptions,
  createMachine,
  spawn,
  assign as ogAssign,
  send,
  State,
  ActorRefFrom,
  sendParent,
  ActionTypes,
  UpdateObject,
  ActionObject,
} from 'xstate';
import { assign } from '@xstate/immer';
import {
  formMachine,
  FormField,
  FormSchema,
  FormEvent,
  FormContext,
  FormConfig,
  FormMachine,
  options as defaultOptions,
} from './form-machine';
import { TransformerFn } from '../config/transformers';
import { current } from 'immer';
import { pure } from 'xstate/lib/actions';

export interface Step {
  name: string;
  fields: FormField[];
  label?: string;
}

export type FormConfigs =
  | {
      [formName: string]: Partial<FormConfig>;
    }
  | Array<Partial<FormConfig>>;
export interface WizardContext {
  currentStepIndex: number;
  initialSteps: Step[];
  steps?: ActorRefFrom<FormMachine>[];
  currentStep?: ActorRefFrom<FormMachine>;
  validators: FormContext['validators'];
  transformers?: { [fnName: string]: TransformerFn };
  formConfigs?: FormConfigs;
  globalFields?: FormField[];
}

export interface WizardSchema extends StateSchema<WizardContext> {
  states: {
    completion: {};
    completed: {};
    submitted: {};
    exited: {};
  };
}

export interface AddFieldsEvent {
  type: 'ADD_FIELDS';
  fields: FormField[];
}

export interface ReplaceFieldsEvent {
  type: 'REPLACE_FIELDS';
  fields: FormField[];
}

export interface ChildUpdateEvent {
  type: 'FORM_UPDATE';
  fields: FormField[];
  name: string;
}

export interface SetFormFieldsEvent {
  type: 'SET_FORM_FIELDS';
  fields: FormField[];
  name: string;
}

export interface SetFieldsEvent {
  type: 'SET_FIELDS';
  fields: FormField[];
  checkErrors?: boolean;
}

export interface SetGlobalFieldsEvent {
  type: 'SET_GLOBAL';
  fields: FormField[];
}

export type GoToEvent =
  | {
      type: 'GOTO';
      index: number | string;
      stepName?: string;
    }
  | {
      type: 'GOTO';
      stepName: string;
      index?: number | string;
    };

export interface ExitEvent {
  type: 'EXIT';
}
export interface RespawnEvent {
  type: 'RESPAWN';
}

export interface NextEvent {
  type: 'NEXT';
}

export interface ForwardedFormEvent {
  type: ActionTypes.Update;
  state: State<FormContext, FormEvent, FormSchema>;
  id: string;
}

type XStateUpdateEvent = UpdateObject & { type: ActionTypes.Update };

export type WizardEvent = (
  | AddFieldsEvent
  | ReplaceFieldsEvent
  | RespawnEvent
  | ExitEvent
  | GoToEvent
  | NextEvent
  | ChildUpdateEvent
  | SetFieldsEvent
  | SetFormFieldsEvent
  | SetGlobalFieldsEvent
  | FormEvent
  | XStateUpdateEvent
) & {
  meta?: { [key: string]: unknown };
};

export type WizardState = State<WizardContext, WizardEvent, WizardSchema>;

const wizardStates: StateNodeConfig<WizardContext, WizardSchema, WizardEvent> =
  {
    id: 'wizard',
    initial: 'completion',
    on: {
      RESPAWN: {
        actions: ['spawnChildren', 'assignCurrentStep'],
      },
    },
    states: {
      completion: {
        entry: ['spawnChildren', 'assignCurrentStep'],
        on: {
          GOTO: {
            target: 'completion',
            cond: 'checkStepsInBetween',
            actions: 'setCurrentStepIndex',
          },
          NEXT: {
            cond: 'checkStepsInBetween',
            actions: 'goToNext',
          },
          EXIT: { target: 'exited' },
          CHANGE: {
            actions: 'forwardToCurrentStep',
          },
          SUBMIT: {
            actions: 'forwardToCurrentStep',
          },
          FORM_UPDATE: {
            actions: 'updateSteps',
          },
          ADD_FIELDS: {
            actions: 'setFields',
          },
          REPLACE_FIELDS: {
            actions: 'setFields',
          },
          SET_FIELDS: {
            actions: 'setFields',
          },
          SET_FORM_FIELDS: {
            actions: 'setFormFields',
          },
          SET_GLOBAL: {
            actions: 'setGlobalFields',
          },
        },
      },
      completed: {
        on: {
          GOTO: { target: 'completion', actions: ['setCurrentStepIndex'] },
          NEXT: { target: 'submitted' },
          SUBMIT: { target: 'submitted' },
          EXIT: { target: 'exited' },
        },
      },
      submitted: {
        type: 'final',
      },
      exited: {
        type: 'final',
      },
    },
  };

const forwardToCurrentStep = send<WizardContext, WizardEvent>(
  (_context, event) => {
    return {
      ...event,
    };
  },
  { to: (context) => context.currentStep ?? '' }
);

const spawnChildren = ogAssign<WizardContext, WizardEvent>({
  steps: (context) =>
    context.initialSteps.map((s, i) => {
      const snap =
        context.steps && context.steps[i] && context.steps[i].getSnapshot();
      if (snap && !snap.done) return context.steps![i];
      let configOverride: FormConfig | undefined;
      for (const formName in context.formConfigs) {
        if (
          formName === s.name ||
          formName === '_' ||
          (typeof formName === 'number' && formName === i) ||
          (typeof formName === 'string' && parseInt(formName) === i)
        ) {
          configOverride = context.formConfigs[formName] as FormConfig;
        }
      }
      let externalFields = context.initialSteps
        .filter((step) => step.name !== s.name)
        .flatMap((step) => [
          ...step.fields.map((f) => ({
            ...f,
            name: step.name + '.' + f.name,
          })),
        ]);

      const externalFieldsGlobal = context.globalFields || [];

      externalFields = externalFields.concat(externalFieldsGlobal);

      const formMachineInstance = formMachine
        .withContext({
          label: s.label,
          name: s.name,
          fields: s.fields,
          externalFields,
          validators: context.validators,
          transformers: context.transformers ?? {},
        })
        .withConfig({
          ...defaultOptions,
          ...{
            ...configOverride,
            actions: {
              ...defaultOptions.actions,
              ...configOverride?.actions,
              emit: sendParent((ctx) => {
                return {
                  type: 'FORM_UPDATE',
                  name: s.name,
                  fields: ctx.fields,
                };
              }),
              killed: sendParent(() => ({
                type: 'NEXT',
              })),
            },
          },
        });
      return spawn(formMachineInstance, {
        name: s.name,
        sync: true,
      });
    }),
});

const assignCurrentStep = ogAssign<WizardContext, WizardEvent>({
  currentStep: (context) =>
    context.steps ? context.steps[context.currentStepIndex] : context.steps,
});

export const getIndexFromGoToEvent = (
  initialSteps: WizardContext['initialSteps'],
  event: GoToEvent
) => {
  let index =
    typeof event.index === 'string' ? parseInt(event.index) : event.index;
  index =
    index !== undefined
      ? index
      : initialSteps.findIndex((step) => step.name === event.stepName);
  return index;
};

const setCurrentStepIndex = ogAssign<WizardContext, WizardEvent>(
  (context, event) => {
    if (event.type !== 'GOTO') return context;
    let index = getIndexFromGoToEvent(context.initialSteps, event);
    if (index < 0) return context;
    return {
      ...context,
      currentStepIndex: index,
      currentStep: context.steps ? context.steps[index] : undefined,
    };
  }
);

const goToNext = pure<WizardContext, WizardEvent>((context) => {
  let actions: ActionObject<WizardContext, WizardEvent>[] = [];
  const newIndex = context.currentStepIndex + 1;
  if (newIndex < context.initialSteps.length) {
    actions.push(send({ type: 'GOTO', index: newIndex }));
  }
  return actions;
});

const updateSteps = assign<WizardContext, WizardEvent>((context, event) => {
  const foundStepIndex = context.initialSteps.findIndex(
    (step) => step.name === (event as ChildUpdateEvent).name
  );
  if (foundStepIndex > -1) {
    context.initialSteps[foundStepIndex].fields = (
      event as ChildUpdateEvent
    ).fields;
  }
});
const setGlobalFields = assign<WizardContext, WizardEvent>((ctx, event) => {
  if (event.type !== 'SET_GLOBAL' || !ctx.globalFields) return;
  for (const field of event.fields) {
    const i = ctx.globalFields.findIndex((f) => {
      return f.name === field.name;
    });
    if (i < 0) continue;
    ctx.globalFields[i] = { ...current(ctx.globalFields[i]), ...field };
  }
  for (const step of ctx.steps ?? []) {
    if (!step) continue;
    step.send({ type: 'SET', externalFields: event.fields });
  }
});

const setFormFields = assign<WizardContext, WizardEvent>((context, event) => {
  const foundStepIndex = context.initialSteps.findIndex(
    (step) => step.name === (event as ChildUpdateEvent).name
  );
  if (context.steps && foundStepIndex > -1) {
    const formFields =
      context.steps[foundStepIndex].getSnapshot()?.context.fields ?? [];
    const fields = formFields
      .filter((f) =>
        (event as ChildUpdateEvent).fields.find(
          (eventField) => eventField.name === f.name
        )
      )
      .map(
        (f) =>
          (event as ChildUpdateEvent).fields.find(
            (eventField) => eventField.name === f.name
          ) as FormField
      );
    context.steps[foundStepIndex].send({
      type: 'SET',
      fields,
    });
  }
});

const setFields = assign<WizardContext, WizardEvent>((context, event) => {
  if (
    event.type !== 'SET_FIELDS' &&
    event.type !== 'ADD_FIELDS' &&
    event.type !== 'REPLACE_FIELDS'
  )
    return;
  for (const field of event.fields) {
    const foundStepIndex = context.initialSteps.findIndex(
      (step) => step.name === field.formName
    );
    if (event.type === 'SET_FIELDS' && context.steps && foundStepIndex > -1) {
      const formName =
        context.steps[foundStepIndex].getSnapshot()?.context.name;
      const formFields =
        context.steps[foundStepIndex].getSnapshot()?.context.fields ?? [];
      const fields = formFields
        .filter((f) => filterFields(event, f, formName))
        .map((f) => filterFields(event, f, formName) as FormField);
      context.steps[foundStepIndex].send({
        type: 'SET',
        fields,
        checkErrors: event.checkErrors,
      });
    }

    if (event.type === 'ADD_FIELDS' && context.steps) {
      let formFields =
        context.steps[foundStepIndex].getSnapshot()?.context.fields ?? [];
      const eventFields = event.fields.map((eventField) => {
        return {
          name: eventField.name,
          validator: eventField.validator,
          value: eventField.value,
          transformers: eventField.transformers,
        };
      });
      formFields = [...formFields, ...eventFields];
      context.steps[foundStepIndex].send({
        type: 'SET',
        fields: formFields,
        addField: true,
        checkErrors: false,
      });
    }
  }

  if (event.type === 'REPLACE_FIELDS' && context.steps) {
    context.steps[1].send({
      type: 'SET',
      fields: event.fields,
      replaceFields: true,
      checkErrors: false,
    });
  }
});

const filterFields = (event, f, formName) => {
  return event.fields.find(
    (eventField) =>
      eventField.name === f.name && eventField.formName === formName
  );
};

type WizardPredicate = ConditionPredicate<WizardContext, WizardEvent>;

const isComplete: WizardPredicate = ({ steps }) => {
  if (!steps) return true;
  for (const step of steps) {
    if (step.getSnapshot()?.value !== 'completed') return false;
  }
  return true;
};

export const getFirstAvailableStep = (steps: ActorRefFrom<FormMachine>[]) => {
  const reversedSteps = Object.keys(steps).reverse();
  let firstCompleteIndex = -1;
  for (const i of reversedSteps) {
    if (
      steps[i].state.value === 'completed' ||
      steps[i].state.value === 'submitted'
    ) {
      firstCompleteIndex = parseInt(i);
      break;
    }
  }
  let firstIncompleteIndex = -1;
  for (const i of Object.keys(steps)) {
    if (
      steps[i].state.value !== 'completed' &&
      steps[i].state.value !== 'submitted'
    ) {
      firstIncompleteIndex = parseInt(i);
      break;
    }
  }
  firstIncompleteIndex =
    firstIncompleteIndex === -1 ? steps.length - 1 : firstIncompleteIndex;
  let firstAvailableStep =
    firstCompleteIndex === steps.length - 1
      ? firstCompleteIndex
      : firstCompleteIndex + 1;
  if (firstCompleteIndex > firstIncompleteIndex)
    firstAvailableStep = firstIncompleteIndex;
  return firstAvailableStep;
};

const checkStepsInBetween: WizardPredicate = (
  { currentStepIndex, steps, initialSteps },
  event
) => {
  if (!steps) return false;
  const index =
    event.type === 'GOTO'
      ? getIndexFromGoToEvent(initialSteps, event)
      : currentStepIndex + 1;
  if (index < 0) return false;
  if (index <= getFirstAvailableStep(steps)) return true;
  return false;
};

const options: Partial<MachineOptions<WizardContext, WizardEvent>> = {
  guards: { isComplete, checkStepsInBetween },
  actions: {
    goToNext,
    setCurrentStepIndex,
    spawnChildren,
    assignCurrentStep,
    forwardToCurrentStep,
    updateSteps,
    setFields,
    setFormFields,
    setGlobalFields,
  },
};

export const wizardMachine = createMachine<WizardContext, WizardEvent>(
  wizardStates,
  options
);
